import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getActiveComplianceContent } from "../services/complianceService";
import { useAuth } from "./authContext";

const initialState = Object.freeze({
	EULA: [],
	TERMS: null,
	PRIVACY: null,
	SUPPORT: null,
	COOKIE: null,
});

const ComplianceContext = createContext({
	items: initialState,
	loading: false,
	error: null,
	refresh: () => { },
});

export const useCompliance = () => useContext(ComplianceContext);

const normalizePayload = (data = {}) => ({
	EULA: Array.isArray(data.EULA) ? data.EULA : [],
	TERMS: data.TERMS || null,
	PRIVACY: data.PRIVACY || null,
	SUPPORT: data.SUPPORT || null,
	COOKIE: data.COOKIE || null,
});

const ComplianceProvider = ({ children }) => {
	const { isAuthenticated } = useAuth();
	const [items, setItems] = useState(initialState);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const fetchActiveContent = useCallback(async () => {
		if (!isAuthenticated) {
			setItems(initialState);
			return;
		}
		setLoading(true);
		try {
			const response = await getActiveComplianceContent({
				types: "EULA,TERMS,PRIVACY,SUPPORT,COOKIE",
			});
			if (response.status) {
				setItems(normalizePayload(response.data || {}));
				setError(null);
			} else {
				setError(response.message || "Unable to fetch policy data");
			}
		} catch (err) {
			console.error("Failed to load compliance content", err);
			setError("Unable to fetch policy data");
		} finally {
			setLoading(false);
		}
	}, [isAuthenticated]);

	useEffect(() => {
		if (isAuthenticated) {
			fetchActiveContent();
		} else {
			setItems(initialState);
		}
	}, [fetchActiveContent, isAuthenticated]);

	const value = useMemo(
		() => ({
			items,
			loading,
			error,
			refresh: fetchActiveContent,
		}),
		[items, loading, error, fetchActiveContent]
	);

	return <ComplianceContext.Provider value={value}>{children}</ComplianceContext.Provider>;
};

export default ComplianceProvider;
